/**
 * DiagnosePage — AI triage for one hotel site (Track A, A1).
 *
 * Pick a site → "Run diagnosis" → the api-shim gathers a live evidence
 * dossier and Claude returns a structured verdict: probable cause,
 * confidence, and whether tier-1 support can resolve the issue or it
 * must be escalated to network engineering.
 *
 * Layout: picker bar on top, verdict card on the left, history rail on
 * the right (past diagnoses for the selected site).
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Activity,
  AlertTriangle,
  Cpu,
  Headset,
  Loader2,
  Siren,
  Stethoscope,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  SitePicker,
  type PickedSite,
  type SiteVendor,
} from "@/components/app/site-picker";
import { arubaSlug } from "@/lib/aruba-slug";
import { cn } from "@/lib/cn";
import { useSites } from "@/stores/sites";
import { useArubaSites } from "@/stores/aruba-sites";
import { useCliSites } from "@/stores/cli-sites";
import {
  useDiagnose,
  type Confidence,
  type DiagnosisDetail,
  type DiagnosisDossier,
  type DiagnosisReport,
  type DiagnosisRun,
  type Verdict,
} from "@/stores/diagnose";

/* ---------- verdict / confidence presentation ---------- */

const VERDICT_META: Record<
  Verdict,
  { label: string; icon: typeof Siren; badge: BadgeProps["variant"]; band: string }
> = {
  escalate: {
    label: "Escalate to network engineering",
    icon: Siren,
    badge: "err",
    band: "border-rose/25 bg-rose/10 text-rose",
  },
  support_resolvable: {
    label: "Tier-1 support can resolve",
    icon: Headset,
    badge: "ok",
    band: "border-mint/25 bg-mint/10 text-mint",
  },
};

const CONFIDENCE_BADGE: Record<Confidence, BadgeProps["variant"]> = {
  high: "ok",
  medium: "warn",
  low: "neutral",
};

const STATUS_BADGE: Record<string, BadgeProps["variant"]> = {
  online: "ok",
  alerting: "err",
  offline: "warn",
  dormant: "neutral",
};

/* ---------- helpers ---------- */

function timeAgo(iso: string): string {
  const t = Date.parse(iso.replace(" ", "T") + "Z");
  if (Number.isNaN(t)) return iso;
  const secs = Math.max(0, (Date.now() - t) / 1000);
  if (secs < 60) return "just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

interface ShownDiagnosis {
  id: string;
  result: DiagnosisReport;
  dossier: DiagnosisDossier | null;
  model: string | null;
  durationMs: number | null;
  createdAt: string | null;
  createdBy: string | null;
}

const fromRun = (r: DiagnosisRun): ShownDiagnosis => ({
  id: r.id,
  result: r.result,
  dossier: r.dossier,
  model: r.model,
  durationMs: r.elapsedMs,
  createdAt: null,
  createdBy: null,
});

const fromDetail = (d: DiagnosisDetail): ShownDiagnosis => ({
  id: d.id,
  result: d.result,
  dossier: d.dossier,
  model: d.modelName,
  durationMs: d.durationMs,
  createdAt: d.createdAt,
  createdBy: d.createdBy,
});

/* ---------- small presentational pieces ---------- */

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1.5 font-mono text-[10px] uppercase tracking-[0.12em] text-fg-mute">
        {label}
      </div>
      <div className="text-sm leading-relaxed text-fg-dim">{children}</div>
    </div>
  );
}

/* ---------- verdict card ---------- */

function VerdictCard({ shown }: { shown: ShownDiagnosis }) {
  const { result, dossier } = shown;
  const meta = VERDICT_META[result.verdict];
  const Icon = meta.icon;

  return (
    <Card>
      <div className={cn("flex flex-wrap items-center gap-3 border-b px-5 py-3.5", meta.band)}>
        <Icon size={18} className="shrink-0" />
        <span className="text-sm font-bold tracking-tight">{meta.label}</span>
        <Badge variant={CONFIDENCE_BADGE[result.confidence]} size="md">
          {result.confidence} confidence
        </Badge>
        <span className="ml-auto font-mono text-[10.5px] text-fg-mute">
          {shown.model ?? "—"}
          {shown.durationMs != null ? ` · ${(shown.durationMs / 1000).toFixed(1)}s` : ""}
          {" · "}
          {shown.createdAt ? timeAgo(shown.createdAt) : "just now"}
        </span>
      </div>

      <CardContent className="space-y-5">
        <p className="m-0 text-[15px] leading-relaxed text-fg">{result.summary}</p>

        <Field label="Probable root cause">{result.probableCause}</Field>
        <Field label="Why this verdict">{result.verdictReason}</Field>

        <div className="rounded-xl border border-violet/25 bg-aurora-soft/50 px-4 py-3">
          <div className="mb-1 font-mono text-[10px] uppercase tracking-[0.12em] text-violet">
            Recommended action
          </div>
          <div className="text-sm leading-relaxed text-fg">{result.recommendedAction}</div>
        </div>

        {result.affectedDevices.length > 0 ? (
          <Field label={`Affected devices (${result.affectedDevices.length})`}>
            <div className="flex flex-wrap gap-1.5">
              {result.affectedDevices.map((d) => (
                <span
                  key={d}
                  className="rounded-md border border-line bg-surface-tint px-2 py-0.5 font-mono text-[11px] text-fg-dim"
                >
                  {d}
                </span>
              ))}
            </div>
          </Field>
        ) : null}

        {result.evidence.length > 0 ? (
          <Field label="Evidence">
            <ul className="m-0 space-y-1.5 p-0">
              {result.evidence.map((e) => (
                <li key={e} className="flex gap-2.5">
                  <span
                    aria-hidden="true"
                    className="mt-[7px] inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-violet/60"
                  />
                  <span>{e}</span>
                </li>
              ))}
            </ul>
          </Field>
        ) : null}

        {dossier ? (
          <div className="border-t border-line pt-4">
            <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.12em] text-fg-mute">
              Site snapshot at diagnosis time
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="info" size="md">
                {dossier.deviceState.deviceCount} devices
              </Badge>
              {Object.entries(dossier.deviceState.statusTotals)
                .flatMap(([status, n]) =>
                  n > 0
                    ? [
                        <Badge key={status} variant={STATUS_BADGE[status] ?? "neutral"} size="md" dot>
                          {n} {status}
                        </Badge>,
                      ]
                    : [],
                )}
              {dossier.deviceState.unhealthyTruncated ? (
                <span className="font-mono text-[10px] text-fg-mute">
                  (unhealthy list truncated)
                </span>
              ) : null}
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

/* ---------- page ---------- */

export function DiagnosePage() {
  const [params, setParams] = useSearchParams();

  const merakiSites = useSites((s) => s.sites);
  const loadMerakiSites = useSites((s) => s.loadSites);
  const merakiStatus = useSites((s) => s.status);
  const arubaSites = useArubaSites((s) => s.data);
  const loadArubaSites = useArubaSites((s) => s.load);
  const arubaStatus = useArubaSites((s) => s.status);
  const cliSites = useCliSites((s) => s.data);
  const loadCliSites = useCliSites((s) => s.load);
  const cliStatus = useCliSites((s) => s.status);

  const run = useDiagnose((s) => s.run);
  const loadHistory = useDiagnose((s) => s.loadHistory);
  const loadDetail = useDiagnose((s) => s.loadDetail);
  const running = useDiagnose((s) => s.running);
  const loadingHistory = useDiagnose((s) => s.loadingHistory);
  const historyBySlug = useDiagnose((s) => s.historyBySlug);
  const errorBySlug = useDiagnose((s) => s.errorBySlug);

  const [vendor, setVendor] = useState<SiteVendor>("meraki");
  const [site, setSite] = useState<PickedSite | null>(null);
  const [shown, setShown] = useState<ShownDiagnosis | null>(null);

  const slug = site?.slug ?? "";
  const isRunning = !!running[slug];
  const history = historyBySlug[slug] ?? [];
  const runError = errorBySlug[slug];

  const selectSite = useCallback(
    async (s: PickedSite) => {
      setSite(s);
      setVendor(s.vendor);
      setShown(null);
      setParams({ site: s.slug, vendor: s.vendor }, { replace: true });
      await loadHistory(s.slug);
      const hist = useDiagnose.getState().historyBySlug[s.slug] ?? [];
      if (hist.length > 0) {
        const detail = await loadDetail(hist[0]!.id);
        if (detail) setShown(fromDetail(detail));
      }
    },
    [loadHistory, loadDetail, setParams],
  );

  function onVendorChange(v: SiteVendor) {
    if (v === vendor) return;
    setVendor(v);
    setSite(null);
    setShown(null);
  }

  // Honor a ?site=&vendor= deep link once the relevant site list is available.
  useEffect(() => {
    const wanted = params.get("site");
    if (!wanted || site) return;
    const want = wanted.toLowerCase();
    const pv = params.get("vendor");
    const wantVendor: SiteVendor = pv === "aruba" || pv === "cli" ? pv : "meraki";
    if (wantVendor === "meraki") {
      const m = merakiSites.find((s) => s.slug === want);
      if (m) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        void selectSite({ slug: m.slug, code: m.code, org: m.org, vendor: "meraki" });
      } else if (merakiStatus === "idle") {
        void loadMerakiSites();
      }
    } else if (wantVendor === "aruba") {
      const a = arubaSites.find((s) => arubaSlug(s.site_name ?? "") === want);
      if (a) {
        void selectSite({
          slug: arubaSlug(a.site_name ?? ""),
          code: a.site_name ?? "(unnamed site)",
          org: [a.city, a.country].filter(Boolean).join(", ") || "Aruba Central",
          vendor: "aruba",
        });
      } else if (arubaStatus === "idle") {
        void loadArubaSites();
      }
    } else {
      const c = cliSites.find((g) => arubaSlug(g.name) === want);
      if (c) {
        void selectSite({
          slug: arubaSlug(c.name),
          code: c.name,
          org: `${c.hostCount} host${c.hostCount === 1 ? "" : "s"} · Zabbix`,
          vendor: "cli",
        });
      } else if (cliStatus === "idle") {
        void loadCliSites();
      }
    }
  }, [params, site, merakiSites, arubaSites, cliSites, merakiStatus, arubaStatus,
      cliStatus, loadMerakiSites, loadArubaSites, loadCliSites, selectSite]);

  async function onRun() {
    if (!slug || isRunning) return;
    const result = await run(slug, site?.vendor ?? "meraki");
    if (result) {
      setShown(fromRun(result));
    } else {
      const msg = useDiagnose.getState().errorBySlug[slug] || "diagnosis failed";
      toast.error(`Diagnosis failed — ${msg}`);
    }
  }

  async function onPickHistory(id: string) {
    const detail = await loadDetail(id);
    if (detail) setShown(fromDetail(detail));
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-2 text-xs text-fg-mute">
        <Link to="/" className="text-fg-dim hover:text-fg">
          AI
        </Link>
        <span className="opacity-40">/</span>
        <span className="text-fg">Diagnose</span>
      </div>

      <div className="flex items-end justify-between gap-6">
        <div>
          <h1 className="m-0 flex items-center gap-2.5 text-3xl font-extrabold tracking-tight text-aurora">
            <Stethoscope size={26} className="text-violet" />
            Diagnose
          </h1>
          <p className="mt-1.5 max-w-[680px] text-sm text-fg-dim">
            AI triage for a single site. Opus gathers a live evidence dossier — device
            status, recent compliance scans, audit and automation history — and Claude
            returns a structured verdict: probable cause, confidence, and whether tier-1
            support can resolve it or it needs to be escalated to network engineering.
          </p>
        </div>
      </div>

      {/* Picker + run bar */}
      <Card>
        <CardContent className="flex flex-wrap items-center gap-3">
          <div className="min-w-[280px] flex-1">
            <SitePicker
              value={site}
              onPick={(s) => void selectSite(s)}
              vendor={vendor}
              onVendorChange={onVendorChange}
              vendors={["meraki", "aruba", "cli"]}
              placeholder="Select a site to diagnose…"
            />
          </div>
          <Button onClick={() => void onRun()} disabled={!slug || isRunning}>
            {isRunning ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Diagnosing…
              </>
            ) : (
              <>
                <Activity size={14} />
                {shown ? "Re-run diagnosis" : "Run diagnosis"}
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {runError && !isRunning ? (
        <output
          className="flex items-center gap-2.5 rounded-xl border border-rose/30 bg-rose/10 px-3.5 py-2.5 text-sm text-rose"
        >
          <AlertTriangle size={16} />
          <span>Last diagnosis failed — {runError}</span>
        </output>
      ) : null}

      {/* Main + history rail */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
        <div className="min-w-0">
          {isRunning ? (
            <Card>
              <CardContent className="flex flex-col items-center gap-3 py-20 text-center">
                <Loader2 size={28} className="animate-spin text-violet" />
                <div className="text-sm font-semibold text-fg">Gathering evidence and triaging…</div>
                <div className="text-xs text-fg-mute">
                  Fetching live device state and running the structured triage call.
                  This usually takes 20–40 seconds.
                </div>
              </CardContent>
            </Card>
          ) : shown ? (
            <VerdictCard shown={shown} />
          ) : (
            <Card>
              <CardContent className="flex flex-col items-center gap-2 py-20 text-center">
                <Stethoscope size={28} className="text-fg-mute" />
                <div className="text-sm font-semibold text-fg">
                  {site ? "No diagnosis shown" : "Select a site to begin"}
                </div>
                <div className="max-w-[360px] text-xs text-fg-mute">
                  {site
                    ? "Run a fresh diagnosis, or pick a past one from the history rail."
                    : "Choose a site above, then run a diagnosis to get an AI triage verdict."}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* History rail */}
        <div className="min-w-0">
          <Card>
            <div className="flex items-center justify-between border-b border-line px-4 py-3">
              <span className="text-sm font-bold tracking-tight text-fg">History</span>
              {site ? (
                <span className="font-mono text-[10px] text-fg-mute">{history.length}</span>
              ) : null}
            </div>
            <div className="max-h-[640px] overflow-y-auto">
              {!site ? (
                <div className="px-4 py-10 text-center text-xs text-fg-mute">
                  No site selected.
                </div>
              ) : loadingHistory[slug] && history.length === 0 ? (
                <div className="px-4 py-10 text-center text-xs text-fg-mute">Loading…</div>
              ) : history.length === 0 ? (
                <div className="px-4 py-10 text-center text-xs text-fg-mute">
                  No past diagnoses for this site.
                </div>
              ) : (
                history.map((h) => {
                  const active = shown?.id === h.id;
                  return (
                    <button
                      key={h.id}
                      type="button"
                      onClick={() => void onPickHistory(h.id)}
                      className={cn(
                        "flex w-full gap-2.5 border-b border-line/60 px-4 py-3 text-left last:border-b-0 hover:bg-surface-2/50",
                        active && "bg-aurora-soft/60",
                      )}
                    >
                      <span
                        aria-hidden="true"
                        className={cn(
                          "mt-1 inline-block h-2 w-2 shrink-0 rounded-full",
                          h.verdict === "escalate" ? "bg-rose" : "bg-mint",
                        )}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <span
                            className={cn(
                              "text-xs font-semibold",
                              h.verdict === "escalate" ? "text-rose" : "text-mint",
                            )}
                          >
                            {h.verdict === "escalate" ? "Escalate" : "Support"}
                          </span>
                          <Badge variant={CONFIDENCE_BADGE[h.confidence]} size="sm">
                            {h.confidence}
                          </Badge>
                        </div>
                        <p className="m-0 mt-1 line-clamp-2 text-[11.5px] leading-snug text-fg-dim">
                          {h.summary}
                        </p>
                        <div className="mt-1 flex items-center gap-1.5 font-mono text-[10px] text-fg-mute">
                          <Cpu size={10} />
                          <span>{timeAgo(h.createdAt)}</span>
                          <span className="opacity-40">·</span>
                          <span className="truncate">{h.createdBy}</span>
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
